(() => {
  if (customElements.get('cashback-banner') && customElements.get('cashback-button')) return;

  class CashbackTrigger extends HTMLElement {
    connectedCallback() {
      this.openButton = this.querySelector('[data-cashback-open]');
      this.changeButton = this.querySelector('[data-cashback-change]');
      this.selectedNameEl = this.querySelector('[data-cashback-selected-name]');
      this.optionsScript = this.querySelector('[data-cashback-options]');

      this.selectedProperty = '';
      this.selectedVariantId = '';
      this.cartUpdateUnsubscriber = undefined;

      this.onOpenPopup = this.onOpenPopup.bind(this);
      this.onChangePopup = this.onChangePopup.bind(this);
      this.onFormSubmit = this.onFormSubmit.bind(this);
      this.onCartUpdate = this.onCartUpdate.bind(this);

      this.openButton?.addEventListener('click', this.onOpenPopup);
      this.changeButton?.addEventListener('click', this.onChangePopup);
      document.addEventListener('submit', this.onFormSubmit, true);

      this.syncBlockedState();
      this.cartUpdateUnsubscriber = theme.pubsub.subscribe(
        theme.pubsub.PUB_SUB_EVENTS.cartUpdate,
        this.onCartUpdate
      );
    }

    disconnectedCallback() {
      this.openButton?.removeEventListener('click', this.onOpenPopup);
      this.changeButton?.removeEventListener('click', this.onChangePopup);
      document.removeEventListener('submit', this.onFormSubmit, true);
      this.cartUpdateUnsubscriber?.();
    }

    get form() {
      return document.getElementById(this.dataset.productFormId);
    }

    get overlay() {
      return document.getElementById(this.dataset.overlayId || 'CashbackOverlay');
    }

    get propertyLabel() {
      return this.overlay?.dataset.propertyLabel || 'Produkt';
    }

    get mainVariantInput() {
      return this.form?.querySelector('[name="id"]:not([data-cashback-input])');
    }

    get mainQuantityInput() {
      return this.form?.querySelector('[name="quantity"]:not([data-cashback-input])');
    }

    get customerCashback() {
      return this.dataset.customerCashback === 'true';
    }

    getOptions() {
      if (!this.optionsScript) return [];
      try {
        return JSON.parse(this.optionsScript.textContent);
      } catch (error) {
        return [];
      }
    }

    onCartUpdate() {
      requestAnimationFrame(() => this.syncBlockedState());
    }

    hasCashbackInCart() {
      return (
        document.querySelector(
          '[id^="CartDrawer-Item"][data-cashback="true"], [id^="CartItem-"][data-cashback="true"]'
        ) !== null
      );
    }

    isBlocked() {
      return this.customerCashback || this.hasCashbackInCart();
    }

    syncBlockedState(blocked = this.isBlocked()) {
      if (blocked) this.clearSelection();
    }

    onOpenPopup(event) {
      event.preventDefault();
      this.openOverlay(1);
    }

    onChangePopup(event) {
      event.preventDefault();
      this.openOverlay(this.isBlocked() ? 1 : 2);
    }

    openOverlay(startStep) {
      this.overlay?.open({
        banner: this,
        options: this.getOptions(),
        selectedProperty: this.selectedProperty,
        selectedVariantId: this.selectedVariantId,
        startStep
      });
    }

    applySelection(property, variantId) {
      if (!property || !variantId) return;
      if (this.isBlocked()) {
        this.clearSelection();
        return;
      }

      this.selectedProperty = property;
      this.selectedVariantId = String(variantId);

      if (this.selectedNameEl) this.selectedNameEl.textContent = property;
      this.classList.add('is-selected');
      this.ensureFormInputs();
    }

    getCartItem(mainVariantId) {
      if (!this.selectedVariantId || !this.selectedProperty) return null;
      if (this.isBlocked()) {
        this.clearSelection();
        return null;
      }

      const parentId = Number(mainVariantId || this.mainVariantInput?.value || 0);
      const item = {
        id: Number(this.selectedVariantId),
        quantity: 1,
        properties: {
          [this.propertyLabel]: this.selectedProperty
        }
      };

      if (parentId) item.parent_id = parentId;
      return item;
    }

    clearSelection() {
      this.selectedProperty = '';
      this.selectedVariantId = '';
      if (this.selectedNameEl) this.selectedNameEl.textContent = '';
      this.classList.remove('is-selected');
      this.clearFormInputs();
    }

    createHiddenInput(name, datasetKey) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.setAttribute('data-cashback-input', datasetKey);
      return input;
    }

    getOrCreateInput(form, datasetKey, name) {
      let input = form.querySelector(`[data-cashback-input="${datasetKey}"]`);
      if (!input) {
        input = this.createHiddenInput(name, datasetKey);
        form.appendChild(input);
      } else {
        input.name = name;
      }
      return input;
    }

    clearFormInputs() {
      const form = this.form;
      if (!form) return;

      form.querySelectorAll('[data-cashback-input]').forEach((input) => input.remove());

      if (this.mainVariantInput) this.mainVariantInput.disabled = false;
      if (this.mainQuantityInput) this.mainQuantityInput.disabled = false;

      this.mainIdInput = null;
      this.mainQtyInput = null;
      this.cashbackIdInput = null;
      this.cashbackQtyInput = null;
      this.cashbackParentInput = null;
      this.cashbackPropertyInput = null;
    }

    ensureFormInputs() {
      const form = this.form;
      if (!form || !this.selectedProperty || !this.selectedVariantId) return;

      const mainVariantId = this.mainVariantInput?.value || '';
      const mainQuantity = this.mainQuantityInput?.value || '1';

      this.mainIdInput = this.getOrCreateInput(form, 'items-0-id', 'items[0][id]');
      this.mainQtyInput = this.getOrCreateInput(form, 'items-0-quantity', 'items[0][quantity]');
      this.cashbackIdInput = this.getOrCreateInput(form, 'items-1-id', 'items[1][id]');
      this.cashbackQtyInput = this.getOrCreateInput(form, 'items-1-quantity', 'items[1][quantity]');
      this.cashbackParentInput = this.getOrCreateInput(form, 'items-1-parent-id', 'items[1][parent_id]');
      this.cashbackPropertyInput = this.getOrCreateInput(
        form,
        'items-1-property',
        `items[1][properties][${this.propertyLabel}]`
      );

      this.mainIdInput.value = mainVariantId;
      this.mainQtyInput.value = mainQuantity;
      this.cashbackIdInput.value = this.selectedVariantId;
      this.cashbackQtyInput.value = '1';
      this.cashbackParentInput.value = mainVariantId;
      this.cashbackPropertyInput.value = this.selectedProperty;

      if (this.mainVariantInput) this.mainVariantInput.disabled = true;
      if (this.mainQuantityInput) this.mainQuantityInput.disabled = true;
    }

    onFormSubmit(event) {
      if (!this.selectedProperty || !this.selectedVariantId) return;
      if (event.target !== this.form) return;

      if (this.isBlocked()) {
        event.preventDefault();
        event.stopPropagation();
        this.clearSelection();
        return;
      }

      const mainVariantId = this.mainVariantInput?.value || '';
      const mainQuantity = this.mainQuantityInput?.value || '1';

      if (this.mainIdInput) this.mainIdInput.value = mainVariantId;
      if (this.mainQtyInput) this.mainQtyInput.value = mainQuantity;
      if (this.cashbackParentInput) this.cashbackParentInput.value = mainVariantId;
    }
  }

  class CashbackBanner extends CashbackTrigger {}
  class CashbackButton extends CashbackTrigger {}

  if (!customElements.get('cashback-banner')) {
    customElements.define('cashback-banner', CashbackBanner);
  }

  if (!customElements.get('cashback-button')) {
    customElements.define('cashback-button', CashbackButton);
  }
})();
